/**
 * RECUPERACIÓN DE CHUNKS QUE NO CARGAN.
 *
 * ## 🔴 El error que motivó esto
 *
 * `this.errorCb is not a function`, lanzado desde `vendors-…mapbox-gl_css.chunk.js` al abrir y cerrar un aviso de
 * la bandeja. Se vio como un error crítico a pantalla completa.
 *
 * ## 🔑 Lo primero que hay que entender: un `ErrorBoundary` NO lo atrapa
 *
 * Es la parte contraintuitiva y la razón por la que este archivo existe aparte del boundary.
 *
 * `componentDidCatch` solo ve lo que se lanza **durante el render o el ciclo de vida** de un componente. Este
 * error se lanza dentro del manejador `onload`/`onerror` de una etiqueta `<link>` que crea el cargador de webpack
 * — o sea en una devolución de llamada del navegador, **fuera del árbol de React**. Para React nunca ocurrió:
 * aterriza directo en `window.onerror`.
 *
 * 📌 Así que el boundary sirve para lo suyo —que un componente roto no se lleve la aplicación— y **esto** sirve
 * para lo otro. Poner solo el boundary habría dejado el error igual de visible.
 *
 * ## Qué significa el fallo, y por qué la respuesta correcta es RECARGAR
 *
 * Un chunk que no carga casi nunca es un defecto del código. Son dos causas, y las dos se arreglan igual:
 *
 * 1. **Se navegó fuera antes de que terminara.** El `<link>` queda a medias y el cargador se pierde.
 * 2. 🔴 **Se desplegó una versión nueva.** El navegador tiene JavaScript viejo pidiendo archivos con un hash que
 *    ya no existe en el servidor. **Este es el caso grave en producción**, y va a pasar en cada despliegue
 *    mientras alguien tenga la pestaña abierta.
 *
 * ⚠️ En el segundo caso **no hay nada que el usuario pueda hacer con un mensaje de error**: su copia de la
 * aplicación quedó huérfana. La única salida es traer la versión nueva.
 *
 * ## 📌 Por qué este archivo NO reporta
 *
 * Reportar exigiría importar `errorReporter`, y `errorReporter` importa la detección de aquí: un ciclo. Se separó
 * por responsabilidad en lugar de romperlo con un `import()` dinámico —que ya se usa en otro punto del proyecto y
 * es más difícil de seguir—: **esto decide si y cómo se recupera; quien llama decide qué reportar.** Los dos
 * llamadores ya tienen el reportero a mano.
 */

/**
 * Candado de una sola recarga.
 *
 * ⚠️ Vive en `sessionStorage` y no en memoria a propósito: la recarga **descarta la memoria**, así que un candado
 * en una variable no serviría de nada — sería un bucle infinito de recargas, que es peor que el error porque ni
 * deja leer el mensaje.
 *
 * 📌 `sessionStorage` y no `localStorage`: el candado debe durar lo que la pestaña. Con `localStorage`, alguien
 * que tuvo un fallo el lunes no se recuperaría del despliegue del martes.
 */
const RELOAD_GUARD = 'dylo:chunk-reload';

/**
 * ¿Es un fallo de carga de chunk y no un error del código?
 *
 * ⚠️ Se reconoce por el **mensaje** porque webpack no expone un tipo para esto. La lista cubre las variantes que
 * produce CRA 5: el fallo genérico de `import()`, el del cargador de CSS —que es el que se vio, con `errorCb`— y
 * el de un módulo dinámico que no se pudo traer.
 *
 * 🔑 Lista explícita y no un `includes('chunk')`: un error legítimo del negocio que mencionara la palabra "chunk"
 * provocaría una recarga en lugar de un reporte, y el defecto real quedaría **invisible**. Es el peor resultado
 * posible de un mecanismo de recuperación — esconder lo que debía mostrar.
 */
export const isChunkLoadFailure = (input: {
  message?: string | null;
  name?: string | null;
}): boolean => {
  const text = `${input.name || ''} ${input.message || ''}`;
  if (!text.trim()) return false;

  return (
    /ChunkLoadError/i.test(text) ||
    /Loading chunk [\w\d-]+ failed/i.test(text) ||
    /Loading CSS chunk/i.test(text) ||
    /errorCb is not a function/i.test(text) ||
    /Failed to fetch dynamically imported module/i.test(text) ||
    /error loading dynamically imported module/i.test(text)
  );
};

/**
 * ¿Toca recargar? Marca el candado de paso.
 *
 * 🔴 Devuelve `false` en el **segundo** fallo de la misma pestaña. Si ya se recargó y vuelve a fallar, entonces no
 * es una versión vieja: es algo roto de verdad —el archivo no existe en el servidor, o la red lo bloquea—. Ahí
 * toca mostrar el error, no seguir recargando.
 */
export const shouldReloadForChunk = (): boolean => {
  try {
    const alreadyTried = sessionStorage.getItem(RELOAD_GUARD) === '1';
    sessionStorage.setItem(RELOAD_GUARD, '1');
    return !alreadyTried;
  } catch {
    /**
     * ⚠️ `sessionStorage` puede lanzar: modo privado de Safari, cuota llena, políticas de almacenamiento. Sin este
     * `catch` el manejador de errores **se convertiría en el error**, y entonces no queda nada que atrape nada.
     *
     * 📌 Al no poder leer el candado se permite la recarga. Es la opción correcta: una recarga de más es molesta,
     * no recargar nunca deja al usuario con una aplicación huérfana.
     */
    return true;
  }
};

/**
 * Trae la versión nueva.
 *
 * 📌 Función aparte de `shouldReloadForChunk` para que quien llama pueda **reportar antes de saltar**. La recarga
 * descarta el proceso, así que un reporte posterior nunca se mandaría — y sin eso este mecanismo esconderería su
 * propia causa.
 */
export const reloadForChunk = (): void => {
  window.location.reload();
};

/**
 * Limpia el candado.
 *
 * 📌 Lo llama el botón de "reintentar" del `ErrorBoundary`: si la persona decide intentar de nuevo a mano, el
 * siguiente fallo de chunk merece su recarga automática otra vez.
 */
export const clearChunkGuard = (): void => {
  try {
    sessionStorage.removeItem(RELOAD_GUARD);
  } catch {
    /* ver la nota de arriba */
  }
};
